import React from "react";
import { DownloadSimple, Trash, CaretDown, CaretRight, FilePdf, File as FileIcon } from "@phosphor-icons/react";
import { API } from "@/lib/apiClient";

/** Human-readable file size. */
export function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

/** Download an attachment via authenticated fetch (cookie-based) + blob URL. */
export function downloadAttachment(file) {
  fetch(`${API}/attachments/${file.id}/download`, { credentials: "include" })
    .then((r) => r.blob())
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    });
}

/**
 * Group attachments by title (fallback to filename) — revisions of the same
 * drawing share a title. Sorted so latest revision is first.
 */
export function groupByTitle(files) {
  const groups = files.reduce((acc, f) => {
    const key = (f.title || f.original_filename || "Untitled").trim();
    (acc[key] = acc[key] || []).push(f);
    return acc;
  }, {});
  Object.keys(groups).forEach((k) => {
    groups[k].sort((a, b) => (b.revision || "").localeCompare(a.revision || ""));
  });
  return groups;
}

/**
 * DrawingRow — one revision row inside a collapsed drawing group.
 * Extracted from DrawingList.jsx to cut render complexity.
 */
export function DrawingRow({ r, canEdit, onDelete }) {
  return (
    <div className="px-3 py-2 flex items-center gap-2 text-sm">
      <span className="text-[10px] font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm min-w-[36px] text-center">{r.revision || "—"}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] text-slate-700 truncate">{r.original_filename}</div>
        {r.revision_notes && <div className="text-[10.5px] text-slate-500 truncate">{r.revision_notes}</div>}
        <div className="text-[10px] text-slate-400 font-mono-num">{new Date(r.created_at).toLocaleDateString("en-IN")} · {fmtSize(r.size)}</div>
      </div>
      <button onClick={() => downloadAttachment(r)} className="text-slate-500 hover:text-[#EA580C] p-1" title="Download"><DownloadSimple size={13} /></button>
      {canEdit && <button onClick={() => onDelete(r.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={13} /></button>}
    </div>
  );
}

/**
 * DrawingGroup — collapsible group of revisions for one drawing title.
 */
export function DrawingGroup({ title, revs, isOpen, onToggle, canEdit, onDelete }) {
  const latest = revs[0];
  return (
    <div className="border border-slate-200 rounded-sm bg-white" data-testid={`drawing-group-${title.replace(/\s+/g, "-")}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left">
        {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
        {latest.content_type === "application/pdf" ? <FilePdf size={14} className="text-red-600" /> : <FileIcon size={14} className="text-slate-500" />}
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium truncate">{title}</div>
          <div className="text-[10px] text-slate-500">Latest: {latest.revision || "—"} · {revs.length} revision{revs.length === 1 ? "" : "s"}</div>
        </div>
        <span className="text-[10px] font-mono bg-[#EA580C] text-white px-1.5 py-0.5 rounded-sm">{latest.revision || "R0"}</span>
      </button>
      {isOpen && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {revs.map((r) => (
            <DrawingRow key={r.id} r={r} canEdit={canEdit} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
